import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore, useHasHydrated } from "@/stores/authStore";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import {
  useClientPermissions,
  type ClientFeatureKey,
} from "@/hooks/useClientPermissions";

interface ProtectedRouteProps {
  allowedRoles?: string[];
  requireClientFeature?: ClientFeatureKey;
}

export function ProtectedRoute({
  allowedRoles,
  requireClientFeature,
}: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading } = useAuthStore();
  const hasHydrated = useHasHydrated();
  const { can } = useClientPermissions();
  const location = useLocation();

  // Wait for the persisted auth state to finish loading from localStorage
  // before making any redirect decision. Without this, a page refresh
  // briefly sees isAuthenticated=false (the pre-hydration default) and
  // wrongly bounces a logged-in user to /login.
  if (!hasHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" label="Checking authentication..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Day 87: client-tier gating (Owner/Branch Manager/Staff), reads from the
  // same ACCESS_TABLE the Sidebar uses — see useClientPermissions.ts.
  // Only applies when the route explicitly opts in via requireClientFeature;
  // non-client roles (admin/super_admin/etc.) are unaffected since can()
  // only evaluates clientRole for role === 'client' users.
  if (
    requireClientFeature &&
    user?.role === "client" &&
    !can(requireClientFeature)
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
}

// Day 83 fix — added hasHydrated gate to prevent false "not logged in"
// redirect on page refresh (mirrors apps/driver's ProtectedRoute pattern)

// Day 87: added requireClientFeature prop for client-tier route gating
// Day 7 complete
